//=================================================
// Carousels
// Like sliders but with max-height constraint, centered
// scroll-snap items, and dot pagination indicators.
// Currently it only works with exhibition features but
// could be modified to work in other contexts
//=================================================
import { createInitializeOnce } from './utilities';

const initializeOnce = createInitializeOnce();
let resizeListenerAdded = false;

export default function setupCarousels() {
    // Hydrate carousel templates on small/medium screens only.
    // Images inside <template> are never loaded by the browser,
    // preventing unnecessary downloads on large devices.
    document.querySelectorAll('template[data-carousel-template]').forEach((t) => {
        if (window.matchMedia('(max-width: 999px)').matches) {
            // Before hydrating, pre-calculate each image's rendered size and
            // set loading priorities. Images have inline aspect-ratio styles
            // but the browser can't resolve both width:auto + height:auto in
            // this flex context, so we compute the exact pixel dimensions from
            // the aspect ratio and CSS constraints, then set them as inline
            // styles. This gives every image a concrete size before it loads.
            const carousel = t.parentElement.closest('.carousel');
            const cs = getComputedStyle(carousel);
            const maxH = parseFloat(cs.getPropertyValue('--carousel-max-height'));
            const pad = parseFloat(cs.getPropertyValue('--body-padding'));
            const gap = parseFloat(cs.getPropertyValue('--spacing-small'));

            const sectionParam = new URLSearchParams(window.location.search).get('section');
            const initialIndex = sectionParam ? parseInt(sectionParam, 10) - 1 : 0;

            const items = t.content.querySelectorAll('.carousel-item');
            const count = items.length;

            items.forEach((item, i) => {
                const img = item.querySelector('img');
                if (!img) return;

                if (i === initialIndex) {
                    img.setAttribute('fetchpriority', 'high');
                }

                // Parse the original pixel dimensions from the aspect-ratio
                // inline style (e.g. "auto 2400 / 1602" or "2400 / 1602 auto")
                const ratio = img.style.aspectRatio;
                if (!ratio) return;
                const m = ratio.match(/(\d+)\s*\/\s*(\d+)/);
                if (!m) return;

                const natW = parseInt(m[1], 10);
                const natH = parseInt(m[2], 10);

                // Mirror the CSS max-width constraints:
                //   middle items: 100vw - 2*body-padding - 2*spacing-small
                //   first/last:   100vw - 2*body-padding -   spacing-small
                const isEdge = i === 0 || i === count - 1;
                const maxW = window.innerWidth - 2 * pad - (isEdge ? gap : 2 * gap);

                // Scale down to fit constraints while preserving aspect ratio
                let w = natW;
                let h = natH;
                if (h > maxH) { w = w * maxH / h; h = maxH; }
                if (w > maxW) { h = h * maxW / w; w = maxW; }

                img.style.width = `${ Math.round(w) }px`;
                img.style.height = `${ Math.round(h) }px`;
            });

            t.replaceWith(t.content);
        }
    });

    document.querySelectorAll('.carousel').forEach((carousel) => {
        const list = carousel.querySelector('.carousel__list');
        const items = list ? Array.from(list.querySelectorAll('.carousel-item')) : [];
        const dotsContainer = carousel.querySelector('.carousel__dots');

        // With 1 or no items there's nothing to paginate. Leave the carousel
        // unmarked so that if a template later hydrates more items into this
        // same element (small/medium breakpoints) it still gets set up.
        if (!list || items.length <= 1) {
            // Hide dots if only 1 or no items
            if (dotsContainer) dotsContainer.style.display = 'none';
            return;
        }

        // Guard only after we know the carousel is paginatable
        if (initializeOnce(carousel)) {
            if (dotsContainer) dotsContainer.style.display = '';

            const dotsInner = dotsContainer.querySelector('.carousel__dots-track');

            // Create dot indicators inside the inner wrapper
            items.forEach((_, index) => {
                const dot = document.createElement('span');
                dot.className = 'carousel__dot';
                dot.setAttribute('aria-hidden', 'true');
                dotsInner.appendChild(dot);
            });

            const dots = Array.from(dotsInner.querySelectorAll('.carousel__dot'));
            const dotGap = parseFloat(getComputedStyle(dotsInner).gap) || 0;

            // Click an inactive item to scroll it to center
            items.forEach((item, index) => {
                item.addEventListener('click', () => {
                    if (!item.classList.contains('carousel-item--active')) {
                        items[index].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
                    }
                });
            });

            // Find the closest item to the center of the visible area
            const getActiveIndex = () => {
                const containerCenter = list.scrollLeft + list.clientWidth / 2;
                let closestIndex = 0;
                let closestDistance = Infinity;

                items.forEach((item, index) => {
                    const itemCenter = item.offsetLeft + item.offsetWidth / 2;
                    const distance = Math.abs(containerCenter - itemCenter);
                    if (distance < closestDistance) {
                        closestDistance = distance;
                        closestIndex = index;
                    }
                });

                return closestIndex;
            };

            // Target widths for each dot based on distance from active.
            // Must match the CSS values in _carousel.scss.
            const dotTargetWidth = (distance) => {
                if (distance >= 4) return 0;  // --hidden
                if (distance === 3) return 2;  // --farther
                if (distance === 2) return 4;  // --far
                return 6;                      // --active or --near
            };

            // Center the dots track so the active dot sits at the container center.
            const positionDotTrack = (activeIndex) => {
                if (activeIndex < 0 || activeIndex >= dots.length) return;

                // Sum widths + gaps for dots before the active one
                let activeDotCenter = 0;
                for (let i = 0; i < activeIndex; i++) {
                    activeDotCenter += dotTargetWidth(Math.abs(i - activeIndex)) + dotGap;
                }
                activeDotCenter += dotTargetWidth(0) / 2;

                const containerWidth = dotsContainer.clientWidth;
                const shift = (containerWidth / 2) - activeDotCenter;
                dotsInner.style.transform = `translateX(${ shift }px)`;
            };

            // Update active item, dot visibility, state classes, and centering
            const updateDots = () => {
                const activeIndex = getActiveIndex();

                // Toggle active class on carousel items
                items.forEach((item, index) => {
                    item.classList.toggle('carousel-item--active', index === activeIndex);
                });

                // Update dot state classes — overflow: hidden on __dots clips the rest
                dots.forEach((dot, index) => {
                    const distance = Math.abs(index - activeIndex);
                    dot.classList.toggle('carousel__dot--active', distance === 0);
                    dot.classList.toggle('carousel__dot--near', distance === 1);
                    dot.classList.toggle('carousel__dot--far', distance === 2);
                    dot.classList.toggle('carousel__dot--farther', distance === 3);
                    dot.classList.toggle('carousel__dot--hidden', distance >= 4);
                });

                // Sync the mobile dropdown so the user sees the change instantly,
                // without waiting for the Turbo frame to render.
                const select = document.querySelector('.feature-sections-small__select');
                if (select) select.value = String(activeIndex + 1);

                positionDotTrack(activeIndex);
            };

            // Show the "Read more" link, update its href, and hide the section frame
            let lastReadMoreIndex = -1;
            const updateReadMoreLink = () => {
                const activeIndex = getActiveIndex();
                if (activeIndex === lastReadMoreIndex) return;
                lastReadMoreIndex = activeIndex;

                const readMore = document.querySelector('.feature-sections-small__read-more');
                if (!readMore) return;

                const url = items[activeIndex]?.dataset.url;
                if (url) {
                    readMore.href = url;
                    readMore.classList.remove('hidden');
                }

                // Hide the section content on small/medium so the user must tap "Read more".
                // Target the child element so the class disappears when Turbo replaces the content.
                const sectionEl = document.querySelector('#turbo_exhibition_feature_section > .section');
                if (sectionEl) sectionEl.classList.add('hidden-until-read-more');
            };

            let ready = false;
            let scrollRafId = 0;
            list.addEventListener('scroll', () => {
                if (!ready) return;

                // On iOS, momentum scrolling fires scroll events in irregular bursts.
                // Without limiting to framerate, the active class could toggle multiple
                // times per frame, restarting the CSS transition mid-animation and causing
                // poorly timed CSS animations. Now at most one update happens per frame.
                if (scrollRafId) return; // Already scheduled — coalesce
                scrollRafId = requestAnimationFrame(() => {
                    scrollRafId = 0;
                    updateDots();
                    updateReadMoreLink();
                });
            });

            // Expose per-carousel state so the module-level matchMedia listener
            // can disable/re-enable scroll handling across the ≥1000px breakpoint
            // without creating a per-carousel listener that leaks on Turbo re-init.
            carousel._setReady = (val) => { ready = val; };
            carousel._syncToSection = (requestedIndex) => {
                const param = new URLSearchParams(window.location.search).get('section');
                const index = requestedIndex ?? (param ? parseInt(param, 10) - 1 : 0);
                if (index < 0 || index >= items.length) return;

                carousel.classList.add('carousel--no-transition');
                list.style.scrollBehavior = 'auto';
                const center = items[index].offsetLeft + items[index].offsetWidth / 2;
                list.scrollLeft = center - list.clientWidth / 2;
                list.style.scrollBehavior = '';
                lastReadMoreIndex = index;
                updateDots();
                requestAnimationFrame(() => {
                    carousel.classList.remove('carousel--no-transition');
                });
            };

            // Suppress transitions and smooth scrolling during init.
            // The CSS class handles scroll-behavior, dot transitions, and
            // item opacity transitions. Inline override ensures
            // scrollLeft assignment is instant even before the class
            // takes effect in the rendering pipeline.
            carousel.classList.add('carousel--no-transition');
            list.style.scrollBehavior = 'auto';

            // Scroll to the section from the URL param if present.
            // Setting scrollLeft directly is synchronous and doesn't fire
            // scroll events, unlike scrollIntoView.
            const sectionParam = new URLSearchParams(window.location.search).get('section');
            const initialIndex = sectionParam ? parseInt(sectionParam, 10) - 1 : 0;
            if (initialIndex > 0 && initialIndex < items.length) {
                const itemCenter = items[initialIndex].offsetLeft + items[initialIndex].offsetWidth / 2;
                list.scrollLeft = itemCenter - list.clientWidth / 2;
            }

            // Read the active index and set all dot state synchronously.
            // Reading offsetLeft forces a reflow, so layout values are fresh.
            const activeIndex = getActiveIndex();
            lastReadMoreIndex = activeIndex;

            items.forEach((item, i) => {
                item.classList.toggle('carousel-item--active', i === activeIndex);
            });

            dots.forEach((dot, i) => {
                const distance = Math.abs(i - activeIndex);
                dot.classList.toggle('carousel__dot--active', distance === 0);
                dot.classList.toggle('carousel__dot--near', distance === 1);
                dot.classList.toggle('carousel__dot--far', distance === 2);
                dot.classList.toggle('carousel__dot--farther', distance === 3);
                dot.classList.toggle('carousel__dot--hidden', distance >= 4);
            });

            // Position dot track synchronously
            positionDotTrack(activeIndex);

            // Reveal carousel and dots — everything is already in position
            dotsContainer.classList.add('carousel__dots--ready');
            carousel.classList.remove('carousel--loading');

            // Re-enable transitions and scroll listener after one frame
            // so the reveal paint is complete before anything can animate
            requestAnimationFrame(() => {
                list.style.scrollBehavior = '';
                carousel.classList.remove('carousel--no-transition');
                ready = true;
            });
        }
    });

    // Single module-level matchMedia listener (registered once). Handles:
    // 1. Toggling per-carousel ready state across the ≥1000px breakpoint so
    //    stale scroll events don't fire while the carousel is display:none.
    // 2. Re-hydrating/initializing carousels when resizing back to mobile.
    if (!resizeListenerAdded) {
        resizeListenerAdded = true;
        window.matchMedia('(min-width: 1000px)').addEventListener('change', (e) => {
            // Only initialized carousels expose _setReady/_syncToSection; the
            // optional-chaining calls below no-op on any that aren't set up.
            document.querySelectorAll('.carousel').forEach((carousel) => {
                if (e.matches) {
                    carousel._setReady?.(false);
                } else {
                    // Sync carousel to the current section before re-enabling,
                    // since desktop navigation (arrows/sidebar) may have changed
                    // the section while the carousel was display:none.
                    carousel._syncToSection?.();
                    requestAnimationFrame(() => { carousel._setReady?.(true); });
                }
            });

            if (!e.matches) {
                setupCarousels();
            }
        });
    }
}
